import { memo, useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { selectedQuantityPercentage } from 'actions/placeorder';
import { togglePlaceOrderSection } from 'actions/tradingView';
import { UNIT_OF_ASSETS_QUANTITY } from 'constants/constants';
import { isNan, isSpotContract } from 'helpers/utils';
import i18n from 'i18n/config';
import {
  hintTextSelector,
  quantityInputHasErrorSelector,
  quantityInputPrefilledValuesSelector,
  quantityInputValue,
} from 'selectors/placeOrderSelector';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import NumberInput from 'UIKit/InputNumber/NumberInput';

import CurrencyDropdown from './CurrencyDropdown';
import Errors from './Errors';
import Percentages from './Percentages';
import quantityChangeEpic, {
  updateQuantityInputFromContracts,
} from './quantityChange.thunk';
import styles from './QuantityInput.module.scss';

const quantityLabel = i18n.t('trading:quantity');
const spotPlaceholder = i18n.t('trading:enter_qty');

const QuantityInput = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef(null);
  const selectedProduct = useAppSelector(selectedProductSelector);
  const placeHolderValue = isSpotContract(selectedProduct?.contract_type)
    ? spotPlaceholder
    : `1 ${UNIT_OF_ASSETS_QUANTITY.toLowerCase()} = ${selectedProduct?.contract_value} ${
        selectedProduct?.contract_unit_currency
      }`;
  const inputPrefill = useAppSelector(quantityInputPrefilledValuesSelector);
  const hintText = useAppSelector(hintTextSelector);
  const quantityInputHasError = useAppSelector(quantityInputHasErrorSelector);
  const qtyInput = useAppSelector(quantityInputValue);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  useEffect(() => {
    // update the input on click of orderbook
    if (inputPrefill) {
      dispatch(updateQuantityInputFromContracts(inputPrefill));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputPrefill]);

  useEffect(() => {
    inputRef?.current?.changeValue?.(qtyInput);
  }, [qtyInput]);

  useEffect(() => {
    // reset everything when product changes
    dispatch(togglePlaceOrderSection(true));
    dispatch(quantityChangeEpic(null));
    // Remove focus from input when product changes and force hide mobile keyboard
    if (inputRef?.current) {
      // Access the actual DOM input element through the data-testid
      const domInput = document.querySelector(
        '[data-testid="quantity-input"]'
      ) as HTMLInputElement;
      if (domInput) {
        // Method 1: Temporarily make input readonly to force keyboard hide
        domInput.setAttribute('readonly', 'readonly');
        domInput.blur();

        // Remove readonly after a short delay
        setTimeout(() => {
          const currentDomInput = document.querySelector(
            '[data-testid="quantity-input"]'
          ) as HTMLInputElement;
          if (currentDomInput) {
            currentDomInput.removeAttribute('readonly');
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const handleOnChange = e => {
    const value = e?.target?.value;
    setHasUserTyped(true);
    dispatch(selectedQuantityPercentage(null));
    if (value === '' || value === null || value === undefined) {
      dispatch(quantityChangeEpic(null));
    } else {
      dispatch(quantityChangeEpic(value));
    }
  };

  return (
    <>
      <NumberInput
        addLeadingZeroForDecimal
        numberType="decimal"
        data-testid="quantity-input"
        label={quantityLabel}
        defaultValue={isNan(inputPrefill) ? 0 : inputPrefill}
        ref={inputRef}
        className={cx(styles.qtyInput, {
          [styles.placeholderRight]: isSpotContract(selectedProduct?.contract_type),
        })}
        placeholder={placeHolderValue}
        onChange={handleOnChange}
        after={<CurrencyDropdown inputRef={inputRef} />}
        belowClassName={styles.hintText}
        below={hintText}
        hasError={quantityInputHasError}
        min={0}
      />
      <Percentages />
      <Errors hasUserTyped={hasUserTyped} />
    </>
  );
};

export default memo(QuantityInput);
